/**
 * Comprehensive Achievement Definitions for DogTale Daily
 * 50+ achievements across multiple categories
 */

// Achievement rarity levels with XP multipliers
export const ACHIEVEMENT_RARITY = {
  COMMON: { name: 'Common', color: 'text-surface-500', bgColor: 'bg-surface-100', xpMultiplier: 1 },
  UNCOMMON: { name: 'Uncommon', color: 'text-green-500', bgColor: 'bg-green-100', xpMultiplier: 1.5 },
  RARE: { name: 'Rare', color: 'text-blue-500', bgColor: 'bg-blue-100', xpMultiplier: 2 },
  EPIC: { name: 'Epic', color: 'text-purple-500', bgColor: 'bg-purple-100', xpMultiplier: 3 },
  LEGENDARY: { name: 'Legendary', color: 'text-yellow-500', bgColor: 'bg-yellow-100', xpMultiplier: 5 },
};

// Achievement categories
export const ACHIEVEMENT_CATEGORIES = {
  JOURNAL: 'journal',
  SOCIAL: 'social',
  COLLECTION: 'collection',
  GAMIFICATION: 'gamification',
  PET_CARE: 'pet_care',
  STREAK: 'streak',
  SEASON: 'season',
  SPECIAL: 'special',
};

// Category display info
export const CATEGORY_INFO = {
  [ACHIEVEMENT_CATEGORIES.JOURNAL]: {
    name: 'Journaling',
    icon: '📝',
    description: 'Achievements for writing journal entries',
  },
  [ACHIEVEMENT_CATEGORIES.SOCIAL]: {
    name: 'Social',
    icon: '👥',
    description: 'Achievements for social interactions',
  },
  [ACHIEVEMENT_CATEGORIES.COLLECTION]: {
    name: 'Collection',
    icon: '🏷️',
    description: 'Achievements for collecting breeds and locations',
  },
  [ACHIEVEMENT_CATEGORIES.GAMIFICATION]: {
    name: 'Gamification',
    icon: '🎮',
    description: 'Achievements for quests, battles, and gyms',
  },
  [ACHIEVEMENT_CATEGORIES.PET_CARE]: {
    name: 'Pet Care',
    icon: '🐕',
    description: 'Achievements for caring for your pets',
  },
  [ACHIEVEMENT_CATEGORIES.STREAK]: {
    name: 'Streaks',
    icon: '🔥',
    description: 'Achievements for maintaining streaks',
  },
  [ACHIEVEMENT_CATEGORIES.SEASON]: {
    name: 'Season Pass',
    icon: '🎫',
    description: 'Seasonal achievements',
  },
  [ACHIEVEMENT_CATEGORIES.SPECIAL]: {
    name: 'Special',
    icon: '✨',
    description: 'Special and hidden achievements',
  },
};

/**
 * Achievement definitions with check functions
 * Each achievement has:
 * - key: unique identifier
 * - name: display name
 * - description: achievement description
 * - icon: emoji icon
 * - category: achievement category
 * - rarity: achievement rarity
 * - xp: base XP reward
 * - checkType: type of data to check against
 * - threshold: value needed to unlock
 * - hidden: whether to hide until unlocked
 */
export const ACHIEVEMENT_DEFINITIONS = {
  // ==================== JOURNAL ACHIEVEMENTS ====================
  first_journal: {
    key: 'first_journal',
    name: 'First Steps',
    description: 'Write your first journal entry',
    icon: '✏️',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 25,
    checkType: 'journal_count',
    threshold: 1,
  },
  journal_enthusiast: {
    key: 'journal_enthusiast',
    name: 'Journal Enthusiast',
    description: 'Write 10 journal entries',
    icon: '📖',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'journal_count',
    threshold: 10,
  },
  journal_regular: {
    key: 'journal_regular',
    name: 'Regular Writer',
    description: 'Write 25 journal entries',
    icon: '📚',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'journal_count',
    threshold: 25,
  },
  journal_dedicated: {
    key: 'journal_dedicated',
    name: 'Dedicated Chronicler',
    description: 'Write 50 journal entries',
    icon: '📕',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'journal_count',
    threshold: 50,
  },
  journal_master: {
    key: 'journal_master',
    name: 'Journal Master',
    description: 'Write 100 journal entries',
    icon: '📜',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'journal_count',
    threshold: 100,
  },
  journal_legend: {
    key: 'journal_legend',
    name: 'Chronicle Legend',
    description: 'Write 365 journal entries',
    icon: '🏆',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'journal_count',
    threshold: 365,
  },
  mood_tracker: {
    key: 'mood_tracker',
    name: 'Mood Tracker',
    description: 'Log 7 different moods in journal entries',
    icon: '🎭',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 75,
    checkType: 'unique_moods',
    threshold: 7,
  },
  consistent_writer: {
    key: 'consistent_writer',
    name: 'Consistent Writer',
    description: 'Write journal entries for 30 consecutive days',
    icon: '📝',
    category: ACHIEVEMENT_CATEGORIES.JOURNAL,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 400,
    checkType: 'journal_streak',
    threshold: 30,
  },

  // ==================== SOCIAL ACHIEVEMENTS ====================
  first_friend: {
    key: 'first_friend',
    name: 'New Friend',
    description: 'Make your first friend',
    icon: '👋',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 25,
    checkType: 'friend_count',
    threshold: 1,
  },
  social_circle: {
    key: 'social_circle',
    name: 'Social Circle',
    description: 'Have 5 friends',
    icon: '👥',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'friend_count',
    threshold: 5,
  },
  social_star: {
    key: 'social_star',
    name: 'Social Star',
    description: 'Have 10 friends',
    icon: '⭐',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'friend_count',
    threshold: 10,
  },
  social_elite: {
    key: 'social_elite',
    name: 'Social Elite',
    description: 'Have 25 friends',
    icon: '🌟',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'friend_count',
    threshold: 25,
  },
  social_celebrity: {
    key: 'social_celebrity',
    name: 'Pet Celebrity',
    description: 'Have 50 friends',
    icon: '💫',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 400,
    checkType: 'friend_count',
    threshold: 50,
  },
  social_butterfly: {
    key: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'React to 50 activities',
    icon: '🦋',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'reactions_given',
    threshold: 50,
  },
  reaction_master: {
    key: 'reaction_master',
    name: 'Reaction Master',
    description: 'React to 200 activities',
    icon: '💕',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'reactions_given',
    threshold: 200,
  },
  conversation_starter: {
    key: 'conversation_starter',
    name: 'Conversation Starter',
    description: 'Create 25 activities',
    icon: '💬',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'activities_created',
    threshold: 25,
  },
  content_creator: {
    key: 'content_creator',
    name: 'Content Creator',
    description: 'Create 100 activities',
    icon: '📱',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 250,
    checkType: 'activities_created',
    threshold: 100,
  },
  popular_post: {
    key: 'popular_post',
    name: 'Popular Post',
    description: 'Receive 10 reactions on a single post',
    icon: '🔥',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 150,
    checkType: 'max_reactions_on_post',
    threshold: 10,
  },
  viral_sensation: {
    key: 'viral_sensation',
    name: 'Viral Sensation',
    description: 'Receive 50 reactions on a single post',
    icon: '🚀',
    category: ACHIEVEMENT_CATEGORIES.SOCIAL,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 400,
    checkType: 'max_reactions_on_post',
    threshold: 50,
  },

  // ==================== COLLECTION ACHIEVEMENTS ====================
  breed_novice: {
    key: 'breed_novice',
    name: 'Breed Novice',
    description: 'Discover 10 different breeds',
    icon: '🐕',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'breed_count',
    threshold: 10,
  },
  breed_enthusiast: {
    key: 'breed_enthusiast',
    name: 'Breed Enthusiast',
    description: 'Discover 25 different breeds',
    icon: '🐩',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'breed_count',
    threshold: 25,
  },
  breed_expert: {
    key: 'breed_expert',
    name: 'Breed Expert',
    description: 'Discover 50 different breeds',
    icon: '🦮',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 250,
    checkType: 'breed_count',
    threshold: 50,
  },
  breed_master: {
    key: 'breed_master',
    name: 'Breed Master',
    description: 'Discover 100 different breeds',
    icon: '🐶',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'breed_count',
    threshold: 100,
  },
  breed_legend: {
    key: 'breed_legend',
    name: 'Breed Encyclopedia',
    description: 'Discover 150 different breeds',
    icon: '📖',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'breed_count',
    threshold: 150,
  },
  location_scout: {
    key: 'location_scout',
    name: 'Location Scout',
    description: 'Visit 10 unique locations',
    icon: '📍',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'location_count',
    threshold: 10,
  },
  location_explorer: {
    key: 'location_explorer',
    name: 'Location Explorer',
    description: 'Visit 25 unique locations',
    icon: '🗺️',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'location_count',
    threshold: 25,
  },
  world_traveler: {
    key: 'world_traveler',
    name: 'World Traveler',
    description: 'Visit 50 unique locations',
    icon: '🌍',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 250,
    checkType: 'location_count',
    threshold: 50,
  },
  globetrotter: {
    key: 'globetrotter',
    name: 'Globetrotter',
    description: 'Visit 100 unique locations',
    icon: '✈️',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'location_count',
    threshold: 100,
  },
  collector: {
    key: 'collector',
    name: 'Collector',
    description: 'Add 50 images to favorites',
    icon: '❤️',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'favorite_count',
    threshold: 50,
  },
  curator: {
    key: 'curator',
    name: 'Curator',
    description: 'Add 150 images to favorites',
    icon: '🖼️',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'favorite_count',
    threshold: 150,
  },
  gallery_owner: {
    key: 'gallery_owner',
    name: 'Gallery Owner',
    description: 'Add 500 images to favorites',
    icon: '🏛️',
    category: ACHIEVEMENT_CATEGORIES.COLLECTION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'favorite_count',
    threshold: 500,
  },

  // ==================== GAMIFICATION ACHIEVEMENTS ====================
  quest_rookie: {
    key: 'quest_rookie',
    name: 'Quest Rookie',
    description: 'Complete 5 quests',
    icon: '📋',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'quests_completed',
    threshold: 5,
  },
  quest_adventurer: {
    key: 'quest_adventurer',
    name: 'Quest Adventurer',
    description: 'Complete 25 quests',
    icon: '🗡️',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 150,
    checkType: 'quests_completed',
    threshold: 25,
  },
  quest_hero: {
    key: 'quest_hero',
    name: 'Quest Hero',
    description: 'Complete 50 quests',
    icon: '🦸',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 300,
    checkType: 'quests_completed',
    threshold: 50,
  },
  quest_champion: {
    key: 'quest_champion',
    name: 'Quest Champion',
    description: 'Complete 100 quests',
    icon: '🏅',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'quests_completed',
    threshold: 100,
  },
  quest_legend: {
    key: 'quest_legend',
    name: 'Quest Legend',
    description: 'Complete 200 quests',
    icon: '👑',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'quests_completed',
    threshold: 200,
  },
  gym_challenger: {
    key: 'gym_challenger',
    name: 'Gym Challenger',
    description: 'Conquer your first gym',
    icon: '🏋️',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 100,
    checkType: 'gyms_conquered',
    threshold: 1,
  },
  gym_fighter: {
    key: 'gym_fighter',
    name: 'Gym Fighter',
    description: 'Conquer 3 gyms',
    icon: '💪',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 200,
    checkType: 'gyms_conquered',
    threshold: 3,
  },
  gym_master: {
    key: 'gym_master',
    name: 'Gym Master',
    description: 'Conquer 5 gyms',
    icon: '🥇',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 400,
    checkType: 'gyms_conquered',
    threshold: 5,
  },
  gym_champion: {
    key: 'gym_champion',
    name: 'Gym Champion',
    description: 'Conquer all gyms',
    icon: '🏆',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'all_gyms_conquered',
    threshold: 1,
  },
  pvp_novice: {
    key: 'pvp_novice',
    name: 'PvP Novice',
    description: 'Complete your first PvP battle',
    icon: '⚔️',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'battles_completed',
    threshold: 1,
  },
  pvp_fighter: {
    key: 'pvp_fighter',
    name: 'PvP Fighter',
    description: 'Complete 10 PvP battles',
    icon: '🥊',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 150,
    checkType: 'battles_completed',
    threshold: 10,
  },
  pvp_warrior: {
    key: 'pvp_warrior',
    name: 'PvP Warrior',
    description: 'Complete 25 PvP battles',
    icon: '🗡️',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 300,
    checkType: 'battles_completed',
    threshold: 25,
  },
  pvp_veteran: {
    key: 'pvp_veteran',
    name: 'PvP Veteran',
    description: 'Complete 50 PvP battles',
    icon: '🎖️',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'battles_completed',
    threshold: 50,
  },
  pvp_champion: {
    key: 'pvp_champion',
    name: 'PvP Champion',
    description: 'Complete 100 PvP battles',
    icon: '🏆',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'battles_completed',
    threshold: 100,
  },
  battle_victor: {
    key: 'battle_victor',
    name: 'Battle Victor',
    description: 'Win 10 PvP battles',
    icon: '🥇',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 200,
    checkType: 'battles_won',
    threshold: 10,
  },
  battle_dominator: {
    key: 'battle_dominator',
    name: 'Battle Dominator',
    description: 'Win 50 PvP battles',
    icon: '💀',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'battles_won',
    threshold: 50,
  },
  undefeated: {
    key: 'undefeated',
    name: 'Undefeated',
    description: 'Win 10 PvP battles in a row',
    icon: '🔥',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'win_streak',
    threshold: 10,
  },
  legendary_streak: {
    key: 'legendary_streak',
    name: 'Legendary Streak',
    description: 'Win 25 PvP battles in a row',
    icon: '⚡',
    category: ACHIEVEMENT_CATEGORIES.GAMIFICATION,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'win_streak',
    threshold: 25,
  },

  // ==================== PET CARE ACHIEVEMENTS ====================
  pet_parent: {
    key: 'pet_parent',
    name: 'Pet Parent',
    description: 'Add your first pet',
    icon: '🐾',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'pet_count',
    threshold: 1,
  },
  pet_family: {
    key: 'pet_family',
    name: 'Pet Family',
    description: 'Have 3 pets',
    icon: '🏠',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'pet_count',
    threshold: 3,
  },
  pet_clan: {
    key: 'pet_clan',
    name: 'Pet Clan',
    description: 'Have 5 pets',
    icon: '👨‍👩‍👧‍👦',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'pet_count',
    threshold: 5,
  },
  virtual_pet_owner: {
    key: 'virtual_pet_owner',
    name: 'Virtual Pet Owner',
    description: 'Create a virtual pet',
    icon: '🎮',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 25,
    checkType: 'has_virtual_pet',
    threshold: 1,
  },
  virtual_pet_caretaker: {
    key: 'virtual_pet_caretaker',
    name: 'Virtual Pet Caretaker',
    description: 'Reach level 10 with your virtual pet',
    icon: '🌟',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 150,
    checkType: 'virtual_pet_level',
    threshold: 10,
  },
  virtual_pet_master: {
    key: 'virtual_pet_master',
    name: 'Virtual Pet Master',
    description: 'Reach level 25 with your virtual pet',
    icon: '⭐',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 300,
    checkType: 'virtual_pet_level',
    threshold: 25,
  },
  virtual_pet_legend: {
    key: 'virtual_pet_legend',
    name: 'Virtual Pet Legend',
    description: 'Reach level 50 with your virtual pet',
    icon: '👑',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 750,
    checkType: 'virtual_pet_level',
    threshold: 50,
  },
  health_tracker: {
    key: 'health_tracker',
    name: 'Health Tracker',
    description: 'Log 10 health records',
    icon: '💊',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'health_records',
    threshold: 10,
  },
  health_guardian: {
    key: 'health_guardian',
    name: 'Health Guardian',
    description: 'Log 50 health records',
    icon: '🏥',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 150,
    checkType: 'health_records',
    threshold: 50,
  },
  health_champion: {
    key: 'health_champion',
    name: 'Health Champion',
    description: 'Log 100 health records',
    icon: '🩺',
    category: ACHIEVEMENT_CATEGORIES.PET_CARE,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 300,
    checkType: 'health_records',
    threshold: 100,
  },

  // ==================== STREAK ACHIEVEMENTS ====================
  day_starter: {
    key: 'day_starter',
    name: 'Day Starter',
    description: 'Log in for 3 consecutive days',
    icon: '🌅',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 25,
    checkType: 'login_streak',
    threshold: 3,
  },
  week_warrior: {
    key: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    icon: '🗓️',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'login_streak',
    threshold: 7,
  },
  fortnight_fighter: {
    key: 'fortnight_fighter',
    name: 'Fortnight Fighter',
    description: 'Maintain a 14-day streak',
    icon: '📅',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 100,
    checkType: 'login_streak',
    threshold: 14,
  },
  month_master: {
    key: 'month_master',
    name: 'Month Master',
    description: 'Maintain a 30-day streak',
    icon: '🔥',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 250,
    checkType: 'login_streak',
    threshold: 30,
  },
  quarter_champion: {
    key: 'quarter_champion',
    name: 'Quarter Champion',
    description: 'Maintain a 90-day streak',
    icon: '💫',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'login_streak',
    threshold: 90,
  },
  year_legend: {
    key: 'year_legend',
    name: 'Year Legend',
    description: 'Maintain a 365-day streak',
    icon: '🏆',
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 2000,
    checkType: 'login_streak',
    threshold: 365,
  },

  // ==================== SEASON PASS ACHIEVEMENTS ====================
  season_starter: {
    key: 'season_starter',
    name: 'Season Starter',
    description: 'Reach level 5 in the season pass',
    icon: '🎫',
    category: ACHIEVEMENT_CATEGORIES.SEASON,
    rarity: ACHIEVEMENT_RARITY.COMMON,
    xp: 50,
    checkType: 'season_level',
    threshold: 5,
  },
  season_regular: {
    key: 'season_regular',
    name: 'Season Regular',
    description: 'Reach level 15 in the season pass',
    icon: '🎟️',
    category: ACHIEVEMENT_CATEGORIES.SEASON,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 150,
    checkType: 'season_level',
    threshold: 15,
  },
  season_pro: {
    key: 'season_pro',
    name: 'Season Pro',
    description: 'Reach level 25 in the season pass',
    icon: '🌟',
    category: ACHIEVEMENT_CATEGORIES.SEASON,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 300,
    checkType: 'season_level',
    threshold: 25,
  },
  season_elite: {
    key: 'season_elite',
    name: 'Season Elite',
    description: 'Reach level 40 in the season pass',
    icon: '💎',
    category: ACHIEVEMENT_CATEGORIES.SEASON,
    rarity: ACHIEVEMENT_RARITY.EPIC,
    xp: 500,
    checkType: 'season_level',
    threshold: 40,
  },
  season_champion: {
    key: 'season_champion',
    name: 'Season Champion',
    description: 'Reach level 50 in the season pass',
    icon: '👑',
    category: ACHIEVEMENT_CATEGORIES.SEASON,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'season_level',
    threshold: 50,
  },

  // ==================== SPECIAL ACHIEVEMENTS ====================
  early_bird: {
    key: 'early_bird',
    name: 'Early Bird',
    description: 'Log in before 6 AM',
    icon: '🌅',
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 75,
    checkType: 'special_early_bird',
    threshold: 1,
    hidden: true,
  },
  night_owl: {
    key: 'night_owl',
    name: 'Night Owl',
    description: 'Log in after midnight',
    icon: '🦉',
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    rarity: ACHIEVEMENT_RARITY.UNCOMMON,
    xp: 75,
    checkType: 'special_night_owl',
    threshold: 1,
    hidden: true,
  },
  ai_conversationalist: {
    key: 'ai_conversationalist',
    name: 'AI Conversationalist',
    description: 'Have 50 AI conversations',
    icon: '🤖',
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    rarity: ACHIEVEMENT_RARITY.RARE,
    xp: 200,
    checkType: 'ai_conversations',
    threshold: 50,
  },
  first_anniversary: {
    key: 'first_anniversary',
    name: 'First Anniversary',
    description: 'Use DogTale Daily for one year',
    icon: '🎂',
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1000,
    checkType: 'account_age_days',
    threshold: 365,
    hidden: true,
  },
  completionist: {
    key: 'completionist',
    name: 'Completionist',
    description: 'Unlock 50 achievements',
    icon: '🏅',
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    rarity: ACHIEVEMENT_RARITY.LEGENDARY,
    xp: 1500,
    checkType: 'achievement_count',
    threshold: 50,
  },
};

// Get all achievement keys
export const ACHIEVEMENT_KEYS = Object.keys(ACHIEVEMENT_DEFINITIONS);

// Get achievements by category
export function getAchievementsByCategory(category) {
  return Object.values(ACHIEVEMENT_DEFINITIONS).filter(a => a.category === category);
}

// Get achievement by key
export function getAchievementDefinition(key) {
  return ACHIEVEMENT_DEFINITIONS[key] || null;
}

// Calculate XP with rarity multiplier
export function calculateAchievementXp(achievement) {
  return Math.round(achievement.xp * achievement.rarity.xpMultiplier);
}

// Get total possible achievements count
export function getTotalAchievementCount() {
  return ACHIEVEMENT_KEYS.length;
}

// Get visible achievements (non-hidden)
export function getVisibleAchievements() {
  return Object.values(ACHIEVEMENT_DEFINITIONS).filter(a => !a.hidden);
}

export default ACHIEVEMENT_DEFINITIONS;
